/* kk-theme-v2 global.js — the ONLY global namespace (window.KK).
   ES module, vanilla, zero dependencies, defensive (never throws on missing DOM).
   Implements the frozen window.KK contract from CONTRACTS.md:
     KK.pubsub  { subscribe(event, cb) -> unsub, publish(event, payload) }
     KK.cart    CartStore: add / change / update / get / reconcile / .state
     KK.money(cents)
     KK.track(eventName, payload)  -> GA4 dataLayer bridge
     KK.i18n    localized buyer-facing strings (see the i18n block below)
   PubSub events + GA4 mapping wired at bottom.

   BUY-PATH ERROR CONTRACT (buy-path audit 2026-07-31 — all callers code to this):
     every KK.cart method rejects with an Error carrying
       err.userMessage  SAFE, already-localized buyer-facing string -> render THIS
       err.code         'network' | 'timeout' | 'unavailable' | 'http' | 'unknown'
       err.status       HTTP status when the failure came from a response, else null
       err.data         parsed JSON error body when there was one, else null
     every request is abort-guarded at 15s inside CartStore — callers must NOT add
     their own timeouts; every failed mutation auto-schedules KK.cart.reconcile(). */

(function () {
  'use strict';

  /* ───────────────────────── PubSub ───────────────────────── */
  const subscribers = Object.create(null);

  function subscribe(event, callback) {
    if (typeof event !== 'string' || typeof callback !== 'function') {
      return function () {};
    }
    (subscribers[event] || (subscribers[event] = new Set())).add(callback);
    // returns unsubscribe fn
    return function unsubscribe() {
      const set = subscribers[event];
      if (set) { set.delete(callback); }
    };
  }

  function publish(event, payload) {
    const set = subscribers[event];
    if (!set) return;
    // copy to allow mutation during iteration
    Array.from(set).forEach(function (cb) {
      try { cb(payload); } catch (err) { console.error('[KK] subscriber error for', event, err); }
    });
  }

  const pubsub = { subscribe: subscribe, publish: publish };

  /* ───────────────────────── Consent ─────────────────────────
     Bootstraps the Shopify Customer Privacy API and normalises the many ways a
     visitor's decision can arrive (Shopify Consent Mode, the Customer Privacy
     `visitorConsentCollected` document event, and Pandectes' own events).

     Exposes KK.consent = {
       analyticsAllowed() -> bool,   // gates ALL GA4 dataLayer pushes (see below)
       marketingAllowed() -> bool,   // gates PayPal messaging + the deferred chat loader
       onChange(cb) -> unsub         // fires whenever consent state may have changed
     }

     Defensive: if the Customer Privacy API never loads (e.g. a bot, or a country
     with no banner), analyticsAllowed()/marketingAllowed() fall back to the API's
     own default (which for GDPR regions is "denied until collected"). We NEVER
     hard-default to allowed. */
  const consentSubscribers = new Set();
  let _privacyReady = false;

  function _api() {
    try { return window.Shopify && window.Shopify.customerPrivacy ? window.Shopify.customerPrivacy : null; }
    catch (e) { return null; }
  }

  function analyticsAllowed() {
    const api = _api();
    if (!api) return false; // API not present yet → treat as not-yet-allowed
    try {
      if (typeof api.analyticsProcessingAllowed === 'function') return !!api.analyticsProcessingAllowed();
    } catch (e) { /* noop */ }
    return false;
  }

  function marketingAllowed() {
    const api = _api();
    if (!api) return false;
    try {
      if (typeof api.marketingAllowed === 'function') return !!api.marketingAllowed();
    } catch (e) { /* noop */ }
    return false;
  }

  // Notify local subscribers AND push a Google Consent Mode v2 update reflecting
  // the current state, so gtag-based tags inside GTM ungate the moment consent
  // is granted (GTM itself loads immediately; consent mode does the gating).
  function _fireConsentChange() {
    const analytics = analyticsAllowed();
    const marketing = marketingAllowed();
    try {
      // gtag() is defined by the Consent Mode bootstrap in consent-scripts.liquid.
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          ad_storage: marketing ? 'granted' : 'denied',
          ad_user_data: marketing ? 'granted' : 'denied',
          ad_personalization: marketing ? 'granted' : 'denied',
          analytics_storage: analytics ? 'granted' : 'denied'
        });
      }
    } catch (e) { /* never throw */ }
    Array.from(consentSubscribers).forEach(function (cb) {
      try { cb({ analytics: analytics, marketing: marketing }); } catch (e) { console.error('[KK] consent subscriber error', e); }
    });
    // Any newly-granted analytics consent should flush queued GA4 events.
    if (analytics) _flushTrackQueue();
  }

  function onConsentChange(cb) {
    if (typeof cb !== 'function') return function () {};
    consentSubscribers.add(cb);
    return function () { consentSubscribers.delete(cb); };
  }

  // Register document-level consent signals ONCE. Called after loadFeatures.
  function _wireConsentEvents() {
    // Shopify Customer Privacy API — fires after the banner collects a decision.
    document.addEventListener('visitorConsentCollected', _fireConsentChange);
    // Pandectes CMP dispatches its own events; listen broadly so we catch its model.
    document.addEventListener('pandectes:consent', _fireConsentChange);
    document.addEventListener('cmpConsentUpdated', _fireConsentChange);
  }

  // Bootstrap: ensure the Customer Privacy features are loaded, then wire events
  // and fire an initial state so anything waiting on consent gets today's answer.
  function _bootstrapConsent() {
    const api = _api();
    if (api && typeof api.loadFeatures === 'function') {
      try {
        api.loadFeatures(function () {
          _privacyReady = true;
          _wireConsentEvents();
          _fireConsentChange();
        });
        return;
      } catch (e) { /* fall through */ }
    }
    // Fallback: if customerPrivacy already exposes decision fns, wire directly.
    if (api) {
      _privacyReady = true;
      _wireConsentEvents();
      _fireConsentChange();
      return;
    }
    // API not ready yet — poll briefly for Shopify.customerPrivacy to appear
    // (content_for_header injects it), then bootstrap once.
    let tries = 0;
    const iv = setInterval(function () {
      tries++;
      if (_api()) { clearInterval(iv); _bootstrapConsent(); }
      else if (tries > 40) { clearInterval(iv); /* give up after ~8s */ }
    }, 200);
  }

  const consent = {
    analyticsAllowed: analyticsAllowed,
    marketingAllowed: marketingAllowed,
    onChange: onConsentChange,
    _ready: function () { return _privacyReady; }
  };

  /* ───────────────────────── Money ───────────────────────── */
  // Format integer cents using Shopify.currency + document locale.
  function money(cents) {
    const fmt = window.KK_MONEY_FORMAT;
    if (fmt) {
      const value = Number(cents) || 0;
      const build = (decimalSep, thousandSep, decimals) => {
        const fixed = (value / 100).toFixed(decimals);
        const parts = fixed.split('.');
        const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
        return decimals > 0 ? whole + decimalSep + parts[1] : whole;
      };
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, name) => {
        switch (name) {
          case 'amount_no_decimals': return build('.', ',', 0);
          case 'amount_with_comma_separator': return build(',', '.', 2);
          case 'amount_no_decimals_with_comma_separator': return build(',', '.', 0);
          case 'amount_with_apostrophe_separator': return build('.', '\u2019', 2);
          case 'amount_no_decimals_with_space_separator': return build(',', ' ', 0);
          case 'amount_with_space_separator': return build(',', ' ', 2);
          case 'amount_with_period_and_space_separator': return build('.', ' ', 2);
          default: return build('.', ',', 2);
        }
      });
    }
    const amount = (Number(cents) || 0) / 100;
    let currency = 'EUR';
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        currency = window.Shopify.currency.active;
      }
    } catch (e) { /* noop */ }
    const locale = (document.documentElement.lang || 'de').replace('_', '-');
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
    } catch (e) {
      // fallback if Intl / currency code unavailable
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  /* ───────────────────────── Buyer-facing strings ─────────────────────────
     buy-path audit 2026-07-31 (findings #15 / #39 / #68): the German buy box used to
     render raw exception text ("Unexpected token '<', \"<!DOCTYPE \"… is not valid
     JSON", "Failed to fetch") because component-buy-buttons.js:106 already read
     `window.KK.i18nAddError` and `<html data-cart-error>` — and NOTHING in the theme
     ever defined either one, so the fallback chain ended at the literal 'Error'.

     This completes that existing mechanism rather than inventing a new global:
       1. an attribute on <html> wins when a Liquid layout sets it, so `| t` can
          become the source of truth without touching this file again;
       2. otherwise the table below is used, keyed off <html lang>.

     The wording MIRRORS locales/*.json (cart.error_* / cart.discount_*) — global.js
     is a plain .js asset and cannot run the `t` filter. DE is the source of truth;
     change both together. */
  const I18N_STRINGS = {
    de: {
      cartErrorAdd: 'Der Artikel konnte nicht hinzugefügt werden. Bitte versuche es erneut.',
      cartErrorQuantity: 'Die gewünschte Menge ist nicht verfügbar.',
      cartErrorNetwork: 'Verbindungsproblem. Bitte prüfe deine Internetverbindung und versuche es erneut.',
      cartErrorTimeout: 'Das hat zu lange gedauert. Bitte versuche es erneut.',
      cartErrorGeneric: 'Etwas hat nicht geklappt. Bitte versuche es erneut.',
      cartDiscountNetworkError: 'Verbindungsproblem — bitte erneut versuchen.',
      cartDiscountInvalid: 'Dieser Rabattcode ist ungültig.'
    },
    en: {
      cartErrorAdd: 'This item could not be added. Please try again.',
      cartErrorQuantity: 'The requested quantity is not available.',
      cartErrorNetwork: 'Connection problem. Please check your internet connection and try again.',
      cartErrorTimeout: 'That took too long. Please try again.',
      cartErrorGeneric: 'Something went wrong. Please try again.',
      cartDiscountNetworkError: 'Connection problem — please try again.',
      cartDiscountInvalid: 'This discount code is not valid.'
    },
    fr: {
      cartErrorAdd: "Cet article n'a pas pu être ajouté. Veuillez réessayer.",
      cartErrorQuantity: "La quantité demandée n'est pas disponible.",
      cartErrorNetwork: 'Problème de connexion. Vérifiez votre connexion Internet et réessayez.',
      cartErrorTimeout: 'Cela a pris trop de temps. Veuillez réessayer.',
      cartErrorGeneric: "Une erreur s'est produite. Veuillez réessayer.",
      cartDiscountNetworkError: 'Problème de connexion — veuillez réessayer.',
      cartDiscountInvalid: "Ce code de réduction n'est pas valide."
    },
    nl: {
      cartErrorAdd: 'Dit artikel kon niet worden toegevoegd. Probeer het opnieuw.',
      cartErrorQuantity: 'De gewenste hoeveelheid is niet beschikbaar.',
      cartErrorNetwork: 'Verbindingsprobleem. Controleer je internetverbinding en probeer het opnieuw.',
      cartErrorTimeout: 'Dit duurde te lang. Probeer het opnieuw.',
      cartErrorGeneric: 'Er is iets misgegaan. Probeer het opnieuw.',
      cartDiscountNetworkError: 'Verbindingsprobleem — probeer het opnieuw.',
      cartDiscountInvalid: 'Deze kortingscode is niet geldig.'
    },
    fi: {
      cartErrorAdd: 'Tuotetta ei voitu lisätä. Yritä uudelleen.',
      cartErrorQuantity: 'Haluttua määrää ei ole saatavilla.',
      cartErrorNetwork: 'Yhteysongelma. Tarkista internetyhteytesi ja yritä uudelleen.',
      cartErrorTimeout: 'Tämä kesti liian kauan. Yritä uudelleen.',
      cartErrorGeneric: 'Jokin meni pieleen. Yritä uudelleen.',
      cartDiscountNetworkError: 'Yhteysongelma — yritä uudelleen.',
      cartDiscountInvalid: 'Tämä alennuskoodi ei kelpaa.'
    },
    it: {
      cartErrorAdd: "Impossibile aggiungere l'articolo. Riprova.",
      cartErrorQuantity: 'La quantità richiesta non è disponibile.',
      cartErrorNetwork: 'Problema di connessione. Controlla la tua connessione a Internet e riprova.',
      cartErrorTimeout: "L'operazione ha richiesto troppo tempo. Riprova.",
      cartErrorGeneric: 'Qualcosa è andato storto. Riprova.',
      cartDiscountNetworkError: 'Problema di connessione — riprova.',
      cartDiscountInvalid: 'Questo codice sconto non è valido.'
    }
  };

  // <html> attribute override per key (the convention component-buy-buttons.js:106
  // already reads for cartErrorAdd). Absent attributes fall through to the table.
  const I18N_ATTRS = {
    cartErrorAdd: 'data-cart-error',
    cartErrorQuantity: 'data-cart-error-quantity',
    cartErrorNetwork: 'data-cart-error-network',
    cartErrorTimeout: 'data-cart-error-timeout',
    cartErrorGeneric: 'data-cart-error-generic',
    cartDiscountNetworkError: 'data-cart-discount-network-error',
    cartDiscountInvalid: 'data-cart-discount-invalid'
  };

  const i18n = (function buildI18n() {
    const root = document.documentElement;
    let lang = 'de';
    try { lang = ((root && root.getAttribute('lang')) || 'de').toLowerCase().slice(0, 2); } catch (e) { /* noop */ }
    const table = I18N_STRINGS[lang] || I18N_STRINGS.de;
    const bag = {};
    Object.keys(I18N_ATTRS).forEach(function (key) {
      let attr = null;
      try { attr = root ? root.getAttribute(I18N_ATTRS[key]) : null; } catch (e) { attr = null; }
      bag[key] = (attr && attr.trim()) || table[key] || I18N_STRINGS.de[key];
    });
    return bag;
  })();

  /* ───────────────────────── CartStore ─────────────────────────
     Only code allowed to talk to /cart endpoints. Keeps last cart in .state,
     publishes 'kk:cart:updated' with { cart, sections, … } after every mutation.

     ERROR CONTRACT (buy-path audit 2026-07-31 — every caller codes against this):
       Every method rejects with an Error carrying
         err.userMessage  a SAFE, already-localized buyer-facing string. RENDER THIS.
         err.code         'network' | 'timeout' | 'unavailable' | 'http' | 'unknown'
         err.status       HTTP status when the failure came from a response
         err.data         parsed JSON error body when there was one, else null
       err.message is set to the same safe string as err.userMessage, so a caller
       that still prints err.message cannot leak a parser/TypeError string.
       Callers must NEVER render a raw exception. Callers must NOT add their own
       timeouts — every request below is already abort-guarded at 15s. */
  const REQUEST_TIMEOUT_MS = 15000;

  function _cartError(code, message) {
    // message doubles as err.message so an un-migrated caller still shows German.
    const err = new Error(message);
    err.code = code;
    err.userMessage = message;
    err.status = null;
    err.data = null;
    return err;
  }

  // 'add' has its own wording ("could not be ADDED"); everything else is generic.
  function _genericMessage(op) {
    return op === 'add' ? i18n.cartErrorAdd : i18n.cartErrorGeneric;
  }

  // fetch() rejected: either our own 15s abort, or the transport died. Nothing here
  // may reach the DOM — TypeError('Failed to fetch') is exactly what used to leak.
  function _transportError(rawErr, timedOut, op) {
    const isAbort = timedOut || (rawErr && (rawErr.name === 'AbortError' || rawErr.code === 20));
    const err = _cartError(isAbort ? 'timeout' : 'network',
      isAbort ? i18n.cartErrorTimeout : i18n.cartErrorNetwork);
    err.cause = rawErr || null;
    return err;
  }

  // Non-2xx response. Shopify's Ajax Cart API answers {status, message, description}
  // with NO "errors" key; `description` is already localized to the shop's language,
  // so it is the only server text safe to show — and only for 422 ("can't add that
  // many" / "not available"). Everything else (429 bot wall, 5xx HTML, 400 stale
  // line key) is a technical condition and gets the generic localized string.
  function _responseError(res, data, op) {
    const description = data && typeof data.description === 'string' ? data.description : '';
    let code = 'http';
    let message = _genericMessage(op);
    if (res.status === 422) {
      code = 'unavailable';
      message = description || i18n.cartErrorQuantity;
    }
    const err = _cartError(code, message);
    err.status = res.status;
    err.data = data || null;
    if (description) { err.description = description; } // legacy field older callers read
    return err;
  }

  const CartStore = {
    state: null,

    /* Single transport for every cart request: 15s AbortController timeout,
       res.ok checked BEFORE parsing, and JSON parsed defensively from text so a
       non-JSON body (bot challenge, WAF interstitial, CDN 5xx page) can never
       throw a SyntaxError out of here. Always returns a promise — never throws
       synchronously, or a caller's .finally() would never be attached. */
    _request: function (url, init, op) {
      let ctrl = null;
      let timer = null;
      let timedOut = false;
      try {
        if (typeof AbortController === 'function') {
          ctrl = new AbortController();
          init.signal = ctrl.signal;
        }
      } catch (e) { ctrl = null; }
      const clear = function () { if (timer) { clearTimeout(timer); timer = null; } };
      if (ctrl) {
        timer = setTimeout(function () {
          timedOut = true;
          try { ctrl.abort(); } catch (e) { /* noop */ }
        }, REQUEST_TIMEOUT_MS);
      }
      let pending;
      try { pending = fetch(url, init); }
      catch (e) { clear(); return Promise.reject(_transportError(e, false, op)); }

      return pending.then(function (res) {
        // Keep the timeout armed across the body read too — a stalled body is a hang.
        return res.text().then(
          function (text) { clear(); return { res: res, text: text }; },
          function (bodyErr) { clear(); throw _transportError(bodyErr, timedOut, op); }
        );
      }, function (rawErr) {
        clear();
        throw _transportError(rawErr, timedOut, op);
      }).then(function (r) {
        let data = null;
        if (r.text) { try { data = JSON.parse(r.text); } catch (e) { data = null; } }
        if (!r.res.ok) { throw _responseError(r.res, data, op); }
        if (data === null) {
          // 200 with a body we cannot parse — treat as unavailable infrastructure.
          const err = _cartError('unknown', _genericMessage(op));
          err.status = r.res.status;
          throw err;
        }
        return data;
      });
    },

    _post: function (url, body, op) {
      let payload;
      try { payload = JSON.stringify(body); }
      catch (e) { return Promise.reject(_cartError('unknown', _genericMessage(op))); }
      return CartStore._request(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: payload
      }, op);
    },

    // Refresh full cart JSON (no mutation). Updates .state, does NOT publish.
    get: function () {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return CartStore._request(root + 'cart.js', { method: 'GET', headers: { 'Accept': 'application/json' } }, 'get')
        .then(function (cart) { CartStore.state = cart; return cart; });
    },

    /* reconcile() -> Promise<cart|null>. NEVER rejects.
       Re-reads /cart.js (the server's truth) and republishes kk:cart:updated with
       { cart, sections:null, reconciled:true } so every surface repaints from
       reality. Use this in a .catch() after ANY failed mutation instead of trusting
       the cached .state — a request can fail AFTER the server already applied it
       (this store's 429 bot wall does exactly that), which makes .state a lie. */
    reconcile: function () {
      return CartStore.get().then(function (cart) {
        pubsub.publish('kk:cart:updated', { cart: cart, sections: null, reconciled: true });
        return cart;
      }, function (err) {
        console.warn('[KK] cart reconcile failed (' + (err && err.code) + ') — surfaces keep their last known state', err);
        return null;
      });
    },

    // Coalesced background reconcile, fired automatically after every failed
    // mutation so no caller has to remember to do it.
    _reconcileTimer: null,
    _scheduleReconcile: function (delay) {
      if (CartStore._reconcileTimer) return;
      CartStore._reconcileTimer = setTimeout(function () {
        CartStore._reconcileTimer = null;
        CartStore.reconcile();
      }, typeof delay === 'number' ? delay : 1200);
    },

    // add(items, {sections}) -> Promise<cartState>
    // items: single {id, quantity, properties} or array of them.
    add: function (items, options) {
      let body;
      try {
        options = options || {};
        const list = Array.isArray(items) ? items : [items];
        body = { items: list };
        if (options.sections) { body.sections = options.sections; }
      } catch (e) {
        // A synchronous throw out of add() would latch every caller's spinner,
        // because their .finally() is attached to a promise that never existed.
        return Promise.reject(_cartError('unknown', _genericMessage('add')));
      }
      return CartStore._post('/cart/add.js', body, 'add').then(function (result) {
        /* buy-path audit 2026-07-31 (findings #9 / #65): a 2xx from /cart/add.js IS
           the purchase. The follow-up /cart.js is enrichment only — its failure must
           degrade the payload, never reject, or the buyer is told the add failed,
           retries, and silently buys the same line twice. */
        return CartStore.get().then(function (cart) {
          return { cart: cart, stale: false };
        }, function (refreshErr) {
          console.warn('[KK] cart refresh after a SUCCESSFUL add failed (' +
            (refreshErr && refreshErr.code) + ') — publishing an optimistic cart and reconciling', refreshErr);
          CartStore._scheduleReconcile();
          return { cart: CartStore._optimisticCart(result), stale: true };
        }).then(function (out) {
          if (out.cart) { CartStore.state = out.cart; }
          const cart = out.cart || CartStore.state;
          pubsub.publish('kk:cart:updated', {
            cart: cart,
            sections: (result && result.sections) || null,
            added: { items: CartStore._addedLineItems(result, cart) },
            stale: !!out.stale
          });
          return cart;
        });
      }, function (err) {
        // The add may still have landed server-side before the response failed.
        CartStore._scheduleReconcile();
        throw err;
      });
    },

    // change(key, quantity, {sections}) -> Promise<cartState>
    // `key` is the LINE-ITEM KEY (contract §7) — never a 1-based line index.
    change: function (key, quantity, options) {
      let body;
      let prev = null;
      try {
        options = options || {};
        prev = CartStore._findLine(key);
        if (/^\d+$/.test(String(key))) {
          console.warn('[KK] cart.change() called with a numeric id "' + key +
            '" — address lines by their line-item KEY, not by index/variant id.');
        }
        body = { id: key, quantity: quantity };
        if (options.sections) { body.sections = options.sections; }
      } catch (e) {
        return Promise.reject(_cartError('unknown', _genericMessage('change')));
      }
      return CartStore._post('/cart/change.js', body, 'change').then(function (cart) {
        CartStore.state = cart;
        pubsub.publish('kk:cart:updated', {
          cart: cart,
          sections: (cart && cart.sections) || null,
          changed: { key: key, quantity: quantity, previous: prev }
        });
        pubsub.publish('kk:quantity:changed', { key: key, quantity: quantity });
        return cart;
      }, function (err) {
        /* Leave .state untouched — the mutation may have been applied before the
           failure, so the cached copy is already wrong and must not be "restored".
           The scheduled reconcile republishes the server truth (findings #20/#24). */
        CartStore._scheduleReconcile();
        throw err;
      });
    },

    // update(updates, {sections}) -> Promise<cartState>
    // `updates` MUST be an object keyed by LINE-ITEM KEY -> quantity (contract §7).
    update: function (updates, options) {
      let body;
      try {
        options = options || {};
        body = { updates: updates || {} };
        if (options.sections) { body.sections = options.sections; }
      } catch (e) {
        return Promise.reject(_cartError('unknown', _genericMessage('update')));
      }
      return CartStore._post('/cart/update.js', body, 'update').then(function (cart) {
        CartStore.state = cart;
        pubsub.publish('kk:cart:updated', {
          cart: cart,
          sections: (cart && cart.sections) || null,
          updated: { updates: body.updates }
        });
        return cart;
      }, function (err) {
        CartStore._scheduleReconcile();
        throw err;
      });
    },

    /* Best-effort local merge, used ONLY when the post-add /cart.js refresh failed.
       Shopify's /cart/add.js returns the affected line item(s) complete with `key`
       and the line's NEW total quantity, so replacing by key is correct. Returns the
       untouched last-known cart when the response carries no keys (nothing safe to
       merge) — the scheduled reconcile repairs it either way. */
    _optimisticCart: function (result) {
      const added = CartStore._addedLineItems(result, null).filter(function (l) { return l && l.key; });
      const base = CartStore.state;
      if (!base || !Array.isArray(base.items)) {
        // No known cart at all — the page-load prime was throttled too. Build a
        // minimal one from the lines add.js just confirmed so the header and drawer
        // have something TRUE to render until the reconcile lands.
        if (!added.length) return null;
        return CartStore._recount({
          items: added.slice(), item_count: 0, total_price: 0, items_subtotal_price: 0,
          original_total_price: 0, total_discount: 0, currency: currencyCode(),
          requires_shipping: true, attributes: {}, note: null,
          discount_codes: [], cart_level_discount_applications: []
        });
      }
      if (!added.length) return base;
      const items = base.items.slice();
      added.forEach(function (line) {
        const idx = items.findIndex(function (it) { return it.key === line.key; });
        if (idx > -1) { items[idx] = line; } else { items.push(line); }
      });
      return CartStore._recount(Object.assign({}, base, { items: items }));
    },

    _recount: function (cart) {
      const items = cart.items || [];
      cart.item_count = items.reduce(function (s, it) { return s + (Number(it.quantity) || 0); }, 0);
      if (items.length && items.every(function (it) { return typeof it.final_line_price === 'number'; })) {
        cart.total_price = items.reduce(function (s, it) { return s + it.final_line_price; }, 0);
        cart.items_subtotal_price = cart.total_price;
        cart.original_total_price = cart.total_price;
      }
      return cart;
    },

    _findLine: function (key) {
      if (!CartStore.state || !CartStore.state.items) return null;
      return CartStore.state.items.find(function (it) { return it.key === key; }) || null;
    },

    // Build enriched added line item(s) for analytics from the /cart/add.js response.
    // add.js returns either the added line object itself or { items: [...] }; each line
    // already carries price/product_title/variant_title/sku. If the response shape is
    // unexpected, fall back to matching by variant id against the fresh cart lines.
    _addedLineItems: function (result, cart) {
      if (result) {
        if (Array.isArray(result.items)) { return result.items; }
        if (result.id) { return [result]; }
      }
      // Fallback: nothing usable in the response — return empty so analytics skips rather
      // than emitting price:0 placeholder items.
      return [];
    }
  };

  /* ───────────────────────── GA4 dataLayer bridge ───────────────────────── */
  window.dataLayer = window.dataLayer || [];

  function currencyCode() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return window.Shopify.currency.active;
      }
    } catch (e) { /* noop */ }
    return 'EUR';
  }

  // GA4 item shape from a Shopify line item / variant-like object.
  function toItem(src, qty) {
    if (!src) return null;
    return {
      item_id: String(src.sku || src.variant_id || src.id || ''),
      item_name: src.product_title || src.title || src.name || '',
      price: (Number(src.price) || 0) / 100,
      quantity: qty != null ? qty : (src.quantity || 1),
      item_variant: src.variant_title || (src.options_with_values ? src.options_with_values.map(function (o) { return o.value; }).join(' / ') : '')
    };
  }

  // Consent-gated queue. Every KK.track push is held until analytics consent is
  // granted, then flushed in order. If consent is denied we DROP events (simple
  // model per Phase-5 contract: analytics consent gates ALL dataLayer pushes).
  const _trackQueue = [];
  const _MAX_QUEUE = 100; // guard against unbounded growth if consent never comes

  function _rawPush(eventName, payload) {
    try {
      window.dataLayer.push({ ecommerce: null }); // clear previous ecommerce object
      window.dataLayer.push(Object.assign({ event: eventName }, payload || {}));
    } catch (e) { /* never throw from analytics */ }
  }

  function _flushTrackQueue() {
    if (!analyticsAllowed()) return;
    while (_trackQueue.length) {
      const ev = _trackQueue.shift();
      _rawPush(ev.event, ev.payload);
    }
  }

  // KK.track — queue an ecommerce-shaped GA4 event; pushed to dataLayer only once
  // analytics consent is granted. Events fired before consent are held (bounded).
  function track(eventName, payload) {
    if (!eventName) return;
    if (analyticsAllowed()) {
      _rawPush(eventName, payload);
      return;
    }
    if (_trackQueue.length < _MAX_QUEUE) {
      _trackQueue.push({ event: eventName, payload: payload });
    }
  }

  function cartToItems(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.map(function (it) { return toItem(it, it.quantity); }).filter(Boolean);
  }

  /* wire cart events → GA4 */
  subscribe('kk:cart:updated', function (payload) {
    const cur = currencyCode();
    if (payload && payload.added) {
      const items = payload.added.items.map(function (i) { return toItem(i, i.quantity); }).filter(Boolean);
      track('add_to_cart', { ecommerce: { currency: cur, items: items } });
    }
    if (payload && payload.changed && payload.changed.previous) {
      const prev = payload.changed.previous;
      if (payload.changed.quantity < (prev.quantity || 0)) {
        const removedQty = (prev.quantity || 0) - payload.changed.quantity;
        track('remove_from_cart', { ecommerce: { currency: cur, items: [toItem(prev, removedQty)].filter(Boolean) } });
      }
    }
  });

  /* wire drawer open → view_cart */
  subscribe('kk:drawer:open', function (payload) {
    if (payload && payload.id && payload.id !== 'cart') return;
    const cart = CartStore.state;
    if (!cart) return;
    track('view_cart', {
      ecommerce: {
        currency: currencyCode(),
        value: (Number(cart.total_price) || 0) / 100,
        items: cartToItems(cart)
      }
    });
  });

  /* begin_checkout — fired once per checkout intent when the user activates any
     checkout control in the cart drawer OR the main cart page. We listen at the
     document level (capture) so it survives cart-drawer inner re-renders and
     works for both the <button name="checkout"> and the plain view/express forms.
     Native submission is NOT blocked — we push the event and let the form proceed. */
  let _beginCheckoutFired = false;
  function _fireBeginCheckout() {
    if (_beginCheckoutFired) return; // navigation is imminent; avoid duplicates
    _beginCheckoutFired = true;
    const cart = CartStore.state;
    track('begin_checkout', {
      ecommerce: {
        currency: currencyCode(),
        value: cart ? (Number(cart.total_price) || 0) / 100 : 0,
        items: cart ? cartToItems(cart) : []
      }
    });
  }
  document.addEventListener('click', function (e) {
    const el = e.target && e.target.closest
      ? e.target.closest('[data-cart-checkout], button[name="checkout"], [name="checkout"]')
      : null;
    if (el) _fireBeginCheckout();
  }, true);
  // Also catch keyboard-activated submits of any cart checkout form.
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || !form.querySelector) return;
    if (form.querySelector('[name="checkout"]')) _fireBeginCheckout();
  }, true);

  /* select_item — delegated click on product cards. product-card.liquid tags its
     link with data-product-card + data-item-id / data-item-name / data-item-price
     (price in cents) / data-item-variant / data-list-name. One listener covers
     every grid on the page (collection, upsell, recommendations, search). Items
     carry the full frozen contract shape {item_id, item_name, price, quantity,
     item_variant}; item_list_name lives on the ecommerce object. */
  document.addEventListener('click', function (e) {
    const card = e.target && e.target.closest ? e.target.closest('[data-product-card]') : null;
    if (!card) return;
    const priceCents = Number(card.getAttribute('data-item-price'));
    track('select_item', {
      ecommerce: {
        currency: currencyCode(),
        item_list_name: card.getAttribute('data-list-name') || '',
        items: [{
          item_id: card.getAttribute('data-item-id') || '',
          item_name: card.getAttribute('data-item-name') || '',
          price: (isNaN(priceCents) ? 0 : priceCents) / 100,
          quantity: 1,
          item_variant: card.getAttribute('data-item-variant') || ''
        }]
      }
    });
  }, true);

  /* view_item_list — collection sections emit ONE call via KK.trackItemList with
     their visible items + list name (collection title). Exposed on KK below. */
  function trackItemList(listName, items) {
    if (!items || !items.length) return;
    track('view_item_list', {
      ecommerce: {
        currency: currencyCode(),
        item_list_name: listName || '',
        items: items
      }
    });
  }

  /* ───────────────────────── Expose ───────────────────────── */
  window.KK = {
    pubsub: pubsub,
    cart: CartStore,
    money: money,
    track: track,
    trackItemList: trackItemList,
    consent: consent,
    // Localized buyer-facing strings for the buy path. Use these for any fallback —
    // never a raw exception. i18nAddError is kept as the name component-buy-buttons.js
    // already reads (buy-path audit 2026-07-31, finding #68).
    i18n: i18n,
    i18nAddError: i18n.cartErrorAdd
  };

  // Prime cart state on load so view_cart has data (non-blocking, defensive).
  // Non-fatal, but never swallowed silently — a failure here means /cart.js is
  // unreachable and every downstream "value: 0" GA4 event is explained by it.
  function _primeCart() {
    CartStore.get().catch(function (err) {
      console.warn('[KK] initial cart prime failed (' + (err && err.code) + ')', err);
    });
  }
  if (document.readyState !== 'loading') {
    _primeCart();
  } else {
    document.addEventListener('DOMContentLoaded', _primeCart, { once: true });
  }

  // Bootstrap consent (loadFeatures + event wiring). Runs after Shopify globals
  // are available; the internal poll handles content_for_header injection order.
  _bootstrapConsent();
})();
